import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { useVerifyForm } from '../features/auth/model/use-verify-form';
import { AuthTopBar } from '../features/auth/ui/auth-top-bar';
import { VerifyCodeEntry } from '../features/auth/ui/verify-code-entry';
import { VerifyMaskedPhonePreview } from '../features/auth/ui/verify-masked-phone-preview';
import { VerifyResendTimer } from '../features/auth/ui/verify-resend-timer';
import {
  mainHeadingTypographyStyles,
  ViewportFrame,
  ViewportPage,
} from '../shared/ui';

const VERIFY_SUCCESS_CONFIRMATION_DELAY_MS = 1200;

function blurActiveInput() {
  if (document.activeElement instanceof HTMLInputElement) {
    document.activeElement.blur();
  }
}

function vibrateOnSuccess() {
  if (typeof navigator.vibrate === 'function') {
    navigator.vibrate(20);
  }
}

function vibrateOnError() {
  if (typeof navigator.vibrate === 'function') {
    navigator.vibrate([18, 50, 18]);
  }
}

const VerifyContent = styled.div.attrs({
  'data-ui': 'verify-page',
})`
  display: flex;
  flex-direction: column;

  & > [data-ui='auth-top-bar'] {
    margin-bottom: ${({ theme }) => theme.scale.px(48)};
  }

  & > [data-ui='verify-intro'] {
    margin-bottom: ${({ theme }) => theme.scale.px(31)};
  }

  & > [data-ui='auth-verify-code-entry'] {
    margin-bottom: ${({ theme }) => theme.scale.px(7)};
  }
`;

const VerifyIntro = styled.div.attrs({
  'data-ui': 'verify-intro',
})`
  display: grid;
  gap: ${({ theme }) => theme.scale.px(7)};
`;

const VerifyTitle = styled.h1`
  ${mainHeadingTypographyStyles};
`;

const VerifySubtitle = styled.p`
  width: ${({ theme }) => theme.scale.px(247)};
  margin: 0;
  white-space: pre-line;
  font-size: ${({ theme }) => theme.scale.px(12)};
  font-weight: 500;
`;

type VerifyLocationState = {
  retryAfter?: number;
  expiresIn?: number;
};

export function VerifyPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const location = useLocation();
  const { retryAfter, expiresIn } = (location.state ??
    {}) as VerifyLocationState;
  const {
    code,
    codeState,
    codeError,
    codeSlots,
    isResendAvailable,
    pasteCodeFromClipboard,
    phonePreview,
    resendCountdown,
    resendCode,
    setCode,
    shouldRedirectToLogin,
  } = useVerifyForm({
    initialRetryAfter: retryAfter,
    initialExpiresIn: expiresIn,
  });
  const subtitleText = t('verify.subtitle', { phone: phonePreview });
  const isCodeEntryLocked = codeState === 'success';

  useEffect(() => {
    if (!shouldRedirectToLogin) {
      return;
    }

    navigate('/login', { replace: true });
  }, [navigate, shouldRedirectToLogin]);

  useEffect(() => {
    if (codeState !== 'success') {
      return;
    }

    blurActiveInput();
    vibrateOnSuccess();

    const confirmationTimeoutId = window.setTimeout(() => {
      navigate('/', { replace: true });
    }, VERIFY_SUCCESS_CONFIRMATION_DELAY_MS);

    return () => {
      window.clearTimeout(confirmationTimeoutId);
    };
  }, [codeState, navigate]);

  useEffect(() => {
    if (codeState !== 'error') {
      return;
    }

    vibrateOnError();
  }, [codeError, codeState]);

  const goBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }

    navigate('/login');
  };

  if (shouldRedirectToLogin) {
    return null;
  }

  return (
    <ViewportPage dataUi="auth-viewport-page">
      <ViewportFrame
        dataUi="auth-viewport-frame"
        paddingBottomPx={28}
        paddingLeftPx={31}
        paddingRightPx={30}
        paddingTopPx={25}
      >
        <VerifyContent>
          <AuthTopBar
            backActionAriaLabel={t('verify.backAria')}
            onBack={goBack}
            showHelpNotificationBadge
          />

          <VerifyIntro>
            <VerifyTitle>{t('verify.title')}</VerifyTitle>
            <VerifySubtitle>
              <VerifyMaskedPhonePreview
                phonePreview={phonePreview}
                subtitleText={subtitleText}
              />
            </VerifySubtitle>
          </VerifyIntro>

          <VerifyCodeEntry
            code={code}
            codeState={codeState}
            codeSlots={codeSlots}
            isInputLocked={isCodeEntryLocked}
            onPasteCodeFromClipboard={pasteCodeFromClipboard}
            onCodeChange={setCode}
          />

          <VerifyResendTimer
            codeState={codeState}
            codeError={codeError ?? undefined}
            isResendAvailable={isResendAvailable}
            onResendCode={resendCode}
            resendCountdown={resendCountdown}
          />
        </VerifyContent>
      </ViewportFrame>
    </ViewportPage>
  );
}
